import api from './api'
import type { ApiResponse } from '../types'

// ================================================================================
// SEARCH SERVICE — unified public-content search across the portal.
// Aggregates published knowledge articles, active campaigns/competitions,
// published resources, published testimonials and verified facilities.
// Public GET /search (no auth). See backend SearchService for ranking.
// ================================================================================

export type SearchType =
  | 'ARTICLE' | 'CAMPAIGN' | 'COMPETITION'
  | 'RESOURCE' | 'TESTIMONIAL' | 'FACILITY'

export interface SearchResult {
  id: number
  type: SearchType
  title: string
  excerpt?: string
  /** Frontend route to view the item. */
  path: string
  imageUrl?: string
  /** Optional category / role / facility-type label. */
  category?: string
  createdAt?: string
}

export interface SearchResults {
  content: SearchResult[]
  totalElements: number
  page: number
  totalPages: number
}

/**
 * A single autocomplete suggestion. `curated=true` are hand-picked topics
 * (multilingual labels resolved server-side via Accept-Language); `curated=false`
 * are live content titles. `path` is where to navigate; `query` is what to run
 * if the user just wants to re-search.
 */
export interface Suggestion {
  label: string
  query: string
  path: string
  type?: SearchType | null
  category?: string | null
  curated: boolean
}

export async function listSearch(params: {
  q?: string
  type?: SearchType
  page?: number
  size?: number
}): Promise<SearchResults> {
  const qs = new URLSearchParams()
  if (params.q) qs.set('q', params.q)
  if (params.type) qs.set('type', params.type)
  qs.set('page', String(params.page ?? 0))
  qs.set('size', String(params.size ?? 12))
  const res = await api.get<ApiResponse<SearchResults>>(`/search?${qs.toString()}`)
  return res.data.data
}

/**
 * Autocomplete suggestions (public, multilingual). Pass an empty `q` to get the
 * curated popular-topics list shown when the search box is focused but empty.
 */
export async function suggestSearch(q: string): Promise<Suggestion[]> {
  const qs = new URLSearchParams()
  if (q) qs.set('q', q)
  const res = await api.get<ApiResponse<Suggestion[]>>(`/search/suggest?${qs.toString()}`)
  return res.data.data
}

// ---- label maps ----

export const SEARCH_TYPE_LABELS: Record<SearchType, string> = {
  ARTICLE: 'Articles',
  CAMPAIGN: 'Campaigns',
  COMPETITION: 'Competitions',
  RESOURCE: 'Resources',
  TESTIMONIAL: 'Stories',
  FACILITY: 'Facilities',
}

export const SEARCH_TYPE_DESCRIPTIONS: Record<SearchType, string> = {
  ARTICLE: 'Knowledge & awareness articles',
  CAMPAIGN: 'Active campaigns',
  COMPETITION: 'Active competitions',
  RESOURCE: 'Guidelines, reports & IEC material',
  TESTIMONIAL: 'Patient & supporter stories',
  FACILITY: 'Verified TB facilities',
}